import { memo, forwardRef, useState, useEffect, useMemo } from 'react';
import toLower from 'lodash/toLower';
import { useLazyQuery, useMutation } from '@apollo/client';
// Skillmore UI Components
import { paramsDiffer } from '@empathco/ui-components/src/helpers/pagination';
import useQueryCounted from '@empathco/ui-components/src/hooks/useQueryCounted';
import useMutationMethod from '@empathco/ui-components/src/hooks/useMutationMethod';
import ActionSnackbar from '@empathco/ui-components/src/elements/ActionSnackbar';
import CardsGrid from '@empathco/ui-components/src/components/CardsGrid';
// local imports
import { MY_ACTIVITIES_QUERY } from '../graphql/MyActivities';
import { EMPLOYEE_ACTIVITIES_QUERY } from '../graphql/EmployeeActivities';
import { UPDATE_ACTIVITY } from '../graphql/UpdateActivity';
import {
  SkillActivity, EmployeeActivity, EmployeeActivityStatus,
  MyActivitiesDocument, MyActivitiesQueryVariables,
  EmployeeActivitiesDocument, EmployeeActivitiesQueryVariables,
  UpdateActivityDocument
} from '../graphql/types';
import { evictEmployeeActivities, getEmployeeActivities } from '../helpers/graphql';
import PaginationControls from '../v3/PaginationControls';
import ActivityCard, { ActivityCardProps } from '../v3/ActivityCard';

type EmployeeSelectedActivitiesProps = {
  readonly supervisor?: boolean | null;
  readonly uid?: string | null;
}

const EmployeeSelectedActivities = forwardRef<HTMLDivElement, EmployeeSelectedActivitiesProps>(({
  supervisor = false,
  uid
}, ref) => {
  // lazy load selected activities
  const myActivities = useQueryCounted({
    data: undefined as unknown as EmployeeActivity,
    key: 'myActivities',
    lazyQuery: useLazyQuery(MY_ACTIVITIES_QUERY as typeof MyActivitiesDocument)
  });
  const employeeActivities = useQueryCounted({
    data: undefined as unknown as EmployeeActivity,
    key: 'employeeActivities',
    lazyQuery: useLazyQuery(EMPLOYEE_ACTIVITIES_QUERY as typeof EmployeeActivitiesDocument)
  });
  const {
    query: getActivities, pending, failed, count, results, variables: prevVars
  } = supervisor ? employeeActivities : myActivities;

  const { mutate: updateActivity, loading: updatePending, failed: updateFailed } = useMutationMethod({
    mutation: useMutation(UPDATE_ACTIVITY as typeof UpdateActivityDocument)
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>();

  const [selectPendingId, setSelectPendingId] = useState<number>();
  const [completePendingId, setCompletePendingId] = useState<number>();

  const componentProps: Partial<ActivityCardProps> = useMemo(() => ({
    supervisor: supervisor ? true : undefined,
    withoutTag: true,
    withCompletedState: true,
    isSelected: !supervisor,
    ...supervisor ? {} : {
      onRemove: (activity: SkillActivity) => {
        const { id: activity_id, employee_activity_id, is_complete } = activity;
        if (is_complete || !activity_id || !employee_activity_id) return;
        setSelectPendingId(activity.id);
        updateActivity({
          variables: { activity_id: employee_activity_id, input: { status: EmployeeActivityStatus.inactive } },
          // TODO: optimistic response
          update: evictEmployeeActivities,
          onCompleted: () => setSelectPendingId(undefined),
          onError: () => setSelectPendingId(undefined)
        });
      },
      onComplete: (activity: SkillActivity) => {
        const { id: activity_id, employee_activity_id, is_complete } = activity;
        if (is_complete || !activity_id || !employee_activity_id) return;
        setCompletePendingId(activity_id);
        updateActivity({
          variables: { activity_id: employee_activity_id, input: { status: EmployeeActivityStatus.completed } },
          // TODO: optimistic response
          update: evictEmployeeActivities,
          onCompleted: () => setCompletePendingId(undefined),
          onError: () => setCompletePendingId(undefined)
        });
      },
      onSelectPendingId: updatePending ? selectPendingId : undefined,
      onCompletePendingId: updatePending ? completePendingId : undefined
    }
  }), [selectPendingId, completePendingId, updatePending, supervisor, updateActivity]);

  const activities = useMemo(() => getEmployeeActivities(results), [results]);

  useEffect(() => {
    if (pageSize && getActivities) {
      const variables: MyActivitiesQueryVariables & EmployeeActivitiesQueryVariables = {
        ...supervisor && uid ? { selected_employee_id: toLower(uid) } : {},
        limit: pageSize
      };
      let curPage = currentPage;
      if (paramsDiffer(prevVars, variables)) {
        curPage = 1;
        setCurrentPage(1);
      }
      variables.offset = pageSize * (curPage - 1);
      getActivities({ variables });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, supervisor, uid, getActivities]); // ignoring `prevVars` changes

  return (
    <>
      <CardsGrid
          ref={ref}
          items={activities}
          variant={supervisor ? 'dark' : 'white'}
          withReloading
          pending={pending}
          failed={failed}
          // disabled={...}
          blendPagination
          component={ActivityCard}
          ComponentProps={componentProps}
          notFoundMessage="hr.dev_plan.selected_activities.empty"
          pagination={(
            <PaginationControls
                settingsId={supervisor ? 'supv_activities' : 'activities'}
                loaded={Boolean(activities)}
                pending={pending}
                loading={pending}
                total={count}
                currentPage={currentPage}
                onPageSelected={setCurrentPage}
                onPageSize={setPageSize}
                disabled={pending || failed}
                totalMessage="hr.dev_plan.selected_activities.pagination"
            />
          )}
      />
      <ActionSnackbar
          severity="error"
          message="skill.development.activity.error"
          open={updateFailed}
      />
    </>
  );
});

EmployeeSelectedActivities.displayName = 'EmployeeSelectedActivities';

export default memo(EmployeeSelectedActivities);
